import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowDownRight, ArrowUpRight, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useTenant } from "@/components/AppShell";
import { Badge, Card, cx, EmptyState, Input, Skeleton, Tip } from "@/components/ui";
import { api, type ReturnsAnalytics } from "@/lib/api";
import { fmtNumber } from "@/lib/format";
import { intlLocale, t, msg } from "@/lib/i18n";

type Days = 30 | 90 | 180 | 365;
const PERIODS: Days[] = [30, 90, 180, 365];
const pct = (x: number | null | undefined, digits = 1) => (x == null ? "—" : `${(x * 100).toLocaleString(intlLocale(), { maximumFractionDigits: digits, minimumFractionDigits: x * 100 < 10 ? digits : 0 })} %`);
const COND = [
  { k: "A", label: msg("A · Wie neu"), cls: "bg-success" },
  { k: "B", label: msg("B · Verpackung beschädigt"), cls: "bg-warning" },
  { k: "C", label: msg("C · Defekt"), cls: "bg-danger" },
  { k: "none", label: msg("ohne Zustand"), cls: "bg-fg-subtle/50" },
] as const;

/** Retouren-Auswertung: Quote, Zustand, Gründe, Verlauf und Artikel mit auffälliger Quote */
export function ReturnsAnalyticsPanel() {
  const { tenantId } = useTenant();
  const [params, setParams] = useSearchParams();
  const days = (Number(params.get("days")) as Days) || 90;
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setTerm(search.trim()), 250);
    return () => clearTimeout(id);
  }, [search]);
  const q = useQuery({ queryKey: ["returns-analytics", tenantId, days, term], queryFn: () => api.returns.analytics(tenantId, { days, ...(term ? { search: term } : {}) }), placeholderData: keepPreviousData, staleTime: 5 * 60_000 });
  const d = q.data;
  const setDays = (x: Days) => setParams((p) => { const n = new URLSearchParams(p); n.set("view", "auswertung"); n.set("days", String(x)); return n; }, { replace: true });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-border" role="radiogroup" aria-label={t("Zeitraum")}>
          {PERIODS.map((p) => (
            <button key={p} role="radio" aria-checked={days === p} onClick={() => setDays(p)} className={cx("h-7 px-3 rounded-md text-[12.5px] font-medium", days === p ? "bg-surface shadow-sm" : "text-fg-muted hover:text-fg")}>
              {p === 365 ? t("12 Monate") : t("{p} Tage", { p })}
            </button>
          ))}
        </div>
        <p className="text-[12px] text-fg-subtle">{t("Quote = zurückgekommene ÷ verkaufte Einheiten im Zeitraum · Rückläufer (nicht zugestellt) zählen getrennt")}</p>
      </div>

      {!d ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label={t("Retourenquote")} value={pct(d.totals.rate)} delta={d.totals.rate != null && d.totals.prev.rate != null ? d.totals.rate - d.totals.prev.rate : null} goodWhenDown hint={d.totals.sold > 0 ? t("{v} von {v2} verkauften Einheiten", { v: fmtNumber(d.totals.units), v2: fmtNumber(d.totals.sold) }) : t("Keine Verkäufe im Zeitraum bekannt – Quote erst mit Aufträgen über Connect (oder importierter Shop-Historie)")} />
            <Kpi label={t("Zurückgekommen")} value={fmtNumber(d.totals.units)} hint={t("{v} Rücksendungen · Vorzeitraum {v2}", { v: fmtNumber(d.totals.cases), v2: fmtNumber(d.totals.prev.units) })} />
            <Card className="p-4">
              <p className="text-[12.5px] text-fg-muted">{t("Zustand")}</p>
              <ConditionBar c={d.totals.conditions} />
              <p className="mt-2 text-[12px] text-fg-subtle">{d.totals.units ? t("{v} wieder verkaufbar (A)", { v: pct(d.totals.conditions.A / d.totals.units, 0) }) : "—"}</p>
            </Card>
            <Kpi label={t("Rückläufer")} value={fmtNumber(d.totals.undeliverable)} hint={t("nicht zustellbar – Adresse oder Annahme verweigert")} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-5">
              <h3 className="font-semibold text-[14px]">{t("Gründe")}</h3>
              <p className="text-[12px] text-fg-subtle mb-3">{t("aus der Prüfung im Lager, je Einheit")}</p>
              {d.reasons.length ? (
                <ul className="space-y-2">
                  {d.reasons.map((r) => {
                    const max = d.reasons[0]!.units || 1;
                    return (
                      <li key={r.id} className="text-[13px]">
                        <div className="flex items-baseline gap-2"><span className="min-w-0 truncate">{t(r.label)}</span><span className="ml-auto tabular-nums text-fg-muted">{fmtNumber(r.units)}{d.totals.units ? <span className="text-fg-subtle"> · {pct(r.units / d.totals.units, 0)}</span> : null}</span></div>
                        <div className="mt-1 h-1.5 rounded-full bg-surface-2 overflow-hidden"><div className="h-full rounded-full bg-ink/70" style={{ width: `${(r.units / max) * 100}%` }} /></div>
                      </li>
                    );
                  })}
                </ul>
              ) : <p className="text-[13px] text-fg-muted">{t("Keine Gründe erfasst.")}</p>}
            </Card>
            <Card className="p-5">
              <h3 className="font-semibold text-[14px]">{t("Verlauf je Woche")}</h3>
              <p className="text-[12px] text-fg-subtle mb-3">{t("zurückgekommene Einheiten · Quote im Tooltip")}</p>
              <WeeklyBars weeks={d.weekly} />
            </Card>
          </div>

          <Card className="overflow-hidden">
            <div className="px-5 pt-4 pb-3 flex flex-wrap items-center gap-3">
              <div>
                <h3 className="font-semibold text-[14px]">{t("Artikel")}</h3>
                <p className="text-[12px] text-fg-subtle">{t("nach Rücksendungen sortiert ·")} <AlertTriangle className="inline size-3 text-warning" /> {t("auffällig: Quote mind. 1,5 × Durchschnitt und mind. 5 Rücksendungen")}</p>
              </div>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("SKU oder Artikel …")} leading={<Search className="size-4" />} suffix={search ? <button onClick={() => setSearch("")} aria-label={t("Suche leeren")}><X className="size-3.5" /></button> : undefined} className="ml-auto w-full sm:w-64" />
            </div>
            {d.products.length === 0 ? (
              <EmptyState icon={<Search className="size-5" />} title={!term && d.unmatched > 0 ? t("Keine Retoure einem Artikel zugeordnet") : t("Keine Retouren im Zeitraum")}>{term ? t("Nichts zu „{term}“.", { term }) : d.unmatched > 0 ? t("Die Rücksendungen passen (noch) zu keinem Artikel im Portal – Zuordnung über FFN-Artikelnummer oder SKU, siehe unten.") : t("In diesem Zeitraum ist nichts zurückgekommen.")}</EmptyState>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead className="text-[12px] text-fg-subtle border-y border-border bg-surface-2/50">
                    <tr>
                      <th className="h-9 pl-5 pr-3 text-left font-medium">{t("Artikel")}</th>
                      <th className="h-9 px-3 text-right font-medium">{t("Verkauft")}</th>
                      <th className="h-9 px-3 text-right font-medium">{t("Zurück")}</th>
                      <th className="h-9 px-3 text-left font-medium">{t("Quote")}</th>
                      <th className="h-9 px-3 text-left font-medium">{t("Zustand")}</th>
                      <th className="h-9 pl-3 pr-5 text-left font-medium">{t("Häufigster Grund")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.products.map((p) => (
                      <tr key={p.productId} className="border-b border-border last:border-0 hover:bg-surface-2/50">
                        <td className="pl-5 pr-3 py-2.5 min-w-[200px]">
                          <Link to={`/products?open=${p.productId}`} className="font-medium hover:underline">{p.name}</Link>
                          <div className="font-mono text-[11.5px] text-fg-muted">{p.sku}</div>
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-fg-muted">{fmtNumber(p.sold)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-medium">{fmtNumber(p.returned)}</td>
                        <td className="px-3 py-2.5 min-w-[140px]">
                          <div className="flex items-center gap-2">
                            <div className="h-1.5 w-16 rounded-full bg-surface-2 overflow-hidden"><div className={cx("h-full rounded-full", p.flagged ? "bg-warning" : "bg-fg-subtle")} style={{ width: `${Math.min(100, (p.rate ?? 0) * 200)}%` }} /></div>
                            <span className={cx("tabular-nums", p.flagged && "text-warning font-semibold")}>{p.sold ? pct(p.rate) : "—"}</span>
                            {p.flagged && <Tip content={t("Deutlich über dem Durchschnitt – Größenangaben, Beschreibung oder Verpackung prüfen")}><AlertTriangle className="size-3.5 text-warning" /></Tip>}
                          </div>
                        </td>
                        <td className="px-3 py-2.5"><MiniCond a={p.A} b={p.B} c={p.C} /></td>
                        <td className="pl-3 pr-5 py-2.5 text-fg-muted">{p.topReason ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {d.unmatched > 0 && (
              <div className="px-5 py-2.5 border-t border-border text-[12px] text-fg-subtle">
                <p>{fmtNumber(d.unmatched)} {d.unmatched === 1 ? t("Einheit") : t("Einheiten")} {t("ohne zugeordneten Artikel (z. B. Fremdware, Artikel nicht im Portal oder abweichende SKU) – in der Gesamtquote enthalten. Sobald der Artikel im Portal existiert, wird beim nächsten Abgleich automatisch zugeordnet.")}</p>
                {!!d.unmatchedSample?.length && (
                  <p className="mt-1">{t("Häufigste:")} {d.unmatchedSample.map((u) => `${u.sku ?? u.jfsku ?? "ohne SKU"}${u.name ? ` (${u.name})` : ""} × ${fmtNumber(u.units)}`).join(" · ")}</p>
                )}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, hint, delta, goodWhenDown }: { label: string; value: string; hint?: string; delta?: number | null; goodWhenDown?: boolean }) {
  const better = delta != null && (goodWhenDown ? delta < 0 : delta > 0);
  return (
    <Card className="p-4">
      <p className="text-[12.5px] text-fg-muted">{t(label)}</p>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-[24px] font-semibold tabular-nums tracking-tight">{value}</span>
        {delta != null && Math.abs(delta) >= 0.0005 && (
          <Badge tone={better ? "success" : "warning"} icon={delta < 0 ? <ArrowDownRight className="size-3" /> : <ArrowUpRight className="size-3" />}>
            {(Math.abs(delta) * 100).toLocaleString(intlLocale(), { maximumFractionDigits: 1 })} {t("Pkt.")}
          </Badge>
        )}
      </div>
      {hint && <p className="text-[12px] text-fg-subtle mt-0.5">{t(hint)}</p>}
    </Card>
  );
}

function ConditionBar({ c }: { c: ReturnsAnalytics["totals"]["conditions"] }) {
  const total = c.A + c.B + c.C + c.none || 1;
  return (
    <div className="mt-2">
      <div className="h-2.5 flex rounded-full overflow-hidden bg-surface-2">
        {COND.map((x) => (c[x.k] ? <Tip key={x.k} content={`${x.label}: ${fmtNumber(c[x.k])} (${pct(c[x.k] / total, 0)})`}><div className={x.cls} style={{ width: `${(c[x.k] / total) * 100}%` }} /></Tip> : null))}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11.5px] text-fg-muted">
        {COND.slice(0, 3).map((x) => <span key={x.k} className="inline-flex items-center gap-1 whitespace-nowrap"><span className={cx("size-2 rounded-full", x.cls)} />{x.k} {pct(c[x.k] / total, 0)}</span>)}
      </div>
    </div>
  );
}

function MiniCond({ a, b, c }: { a: number; b: number; c: number }) {
  const t = a + b + c;
  if (!t) return <span className="text-fg-subtle">—</span>;
  return (
    <Tip content={`A ${a} · B ${b} · C ${c}`}>
      <div className="h-1.5 w-20 flex rounded-full overflow-hidden bg-surface-2">
        <div className="bg-success" style={{ width: `${(a / t) * 100}%` }} />
        <div className="bg-warning" style={{ width: `${(b / t) * 100}%` }} />
        <div className="bg-danger" style={{ width: `${(c / t) * 100}%` }} />
      </div>
    </Tip>
  );
}

function WeeklyBars({ weeks }: { weeks: ReturnsAnalytics["weekly"] }) {
  const max = Math.max(1, ...weeks.map((w) => w.units));
  return (
    <div>
      <div className="h-32 flex items-end gap-1">
        {weeks.map((w) => (
          <Tip key={w.week} content={t("KW ab {v}: {v2} zurück{v3}", { v: new Date(w.week).toLocaleDateString(intlLocale()), v2: fmtNumber(w.units), v3: w.sold ? t(" · Quote {v}", { v: pct(w.units / w.sold) }) : "" })}>
            <div className="flex-1 min-w-0 rounded-t-[3px] bg-ink/60 hover:bg-ink" style={{ height: `${Math.max(w.units ? 4 : 1, (w.units / max) * 100)}%` }} />
          </Tip>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-fg-subtle">
        <span>{weeks[0] ? new Date(weeks[0].week).toLocaleDateString(intlLocale(), { day: "2-digit", month: "2-digit" }) : ""}</span>
        <span>{weeks.at(-1) ? new Date(weeks.at(-1)!.week).toLocaleDateString(intlLocale(), { day: "2-digit", month: "2-digit" }) : ""}</span>
      </div>
    </div>
  );
}
